import { I18N_NAMESPACES } from "@src/appV2/i18n/constants";
import { useTu } from "@src/appV2/i18n/useTu";

import { SettingNumberInput } from "../../components/SettingNumberInput";
import { SettingToggle } from "../../components/SettingToggle";
import { type SettingsPermissions } from "../../types";

interface ComplianceSectionViewProps {
  badgeEnabled: boolean;
  chartingRecallEnabled: boolean;
  gracePeriodHours: number;
  isChartingRecallVisible: boolean;
  isUpdating: boolean;
  permissions: SettingsPermissions;
  qrCodeEnabled: boolean;
  onBadgeToggle: (checked: boolean) => void;
  onChartingRecallToggle: (checked: boolean) => void;
  onGracePeriodChange: (value: number) => void;
  onQrCodeToggle: (checked: boolean) => void;
}

export function ComplianceSectionView({
  badgeEnabled,
  chartingRecallEnabled,
  gracePeriodHours,
  isChartingRecallVisible,
  isUpdating,
  permissions,
  qrCodeEnabled,
  onBadgeToggle,
  onChartingRecallToggle,
  onGracePeriodChange,
  onQrCodeToggle,
}: ComplianceSectionViewProps) {
  const { tu } = useTu(I18N_NAMESPACES.settings);

  return (
    <>
      {permissions.isAdmin && (
        <SettingToggle
          checked={badgeEnabled}
          disabled={isUpdating}
          subtitle={tu(
            "This workplace requires that all Independent Contractors using Clipboard wear a badge at all times during a shift."
          )}
          title={tu("Require a badge during shifts")}
          onChange={onBadgeToggle}
        />
      )}

      <SettingToggle
        checked={qrCodeEnabled}
        disabled={
          isUpdating ||
          (!permissions.isAdmin && !permissions.isFacilityAdmin && !permissions.hasShiftManagement)
        }
        subtitle={tu(
          "When enabled, staff at your workplace can scan a QR code to provide signatures and anonymous feedback at the end of shifts."
        )}
        title={tu("Enable QR code sign out")}
        onChange={onQrCodeToggle}
      />

      {isChartingRecallVisible && (
        <>
          <SettingToggle
            checked={chartingRecallEnabled}
            disabled={isUpdating}
            subtitle={tu(
              "When enabled, professionals can be recalled to complete charting. Professionals with incomplete charting will also be blocked from booking at your workplace."
            )}
            title={tu("Charting Compliance")}
            onChange={onChartingRecallToggle}
          />
          {chartingRecallEnabled && permissions.isAdmin && (
            <SettingNumberInput
              disabled={isUpdating}
              min={1}
              subtitle={tu(
                "The duration in hours a professional has to return and complete charting."
              )}
              title={tu("Charting Compliance Grace Period")}
              unit={tu("hours")}
              value={gracePeriodHours}
              onChange={onGracePeriodChange}
            />
          )}
        </>
      )}
    </>
  );
}
